import { useEffect, useState } from 'react';
import { sb } from '../lib/supabase';
import { useSupabaseAuth } from '../context/AuthContext';
import Badge from './Badge';
import {
  DEFAULT_NOTIFICATION_PREFS,
  DEFAULT_UI_PREFS,
  parseNotificationPrefs,
  parseUiPrefs,
} from '../lib/userPreferences';

const SECTIONS = [
  { id: 'general', label: 'General' },
  { id: 'account', label: 'Account' },
  { id: 'notifications', label: 'Notifications' },
  { id: 'defaults', label: 'Defaults' },
];

const TIMEZONES = [
  'Europe/Amsterdam',
  'Europe/London',
  'Europe/Berlin',
  'Europe/Paris',
  'America/New_York',
  'America/Chicago',
  'America/Los_Angeles',
  'Asia/Singapore',
  'Australia/Sydney',
  'UTC',
];

const LANGUAGES = [
  { value: 'en', label: 'English' },
  { value: 'nl', label: 'Nederlands' },
  { value: 'de', label: 'Deutsch' },
  { value: 'fr', label: 'Français' },
];

function ToggleRow({ label, hint, checked, onChange }) {
  return (
    <label className="settings-toggle-row">
      <span>
        <span className="settings-toggle-label">{label}</span>
        {hint && <span className="settings-toggle-hint">{hint}</span>}
      </span>
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} />
    </label>
  );
}

export default function SettingsModal({ open, onClose }) {
  const { profile, user, refreshProfile } = useSupabaseAuth();
  const [section, setSection] = useState('general');
  const [fullName, setFullName] = useState('');
  const [preferredName, setPreferredName] = useState('');
  const [landingPage, setLandingPage] = useState('home');
  const [timezone, setTimezone] = useState('Europe/Amsterdam');
  const [language, setLanguage] = useState('en');
  const [notifications, setNotifications] = useState({ ...DEFAULT_NOTIFICATION_PREFS });
  const [uiPrefs, setUiPrefs] = useState({ ...DEFAULT_UI_PREFS });
  const [orgName, setOrgName] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (!open || !profile) return;
    setSection('general');
    setFullName(profile.full_name || '');
    setPreferredName(profile.preferred_name || '');
    setLandingPage(profile.default_landing_page === 'last_project' ? 'last_project' : 'home');
    setTimezone(profile.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone || 'Europe/Amsterdam');
    setLanguage(profile.language || 'en');
    setNotifications(parseNotificationPrefs(profile.notification_prefs));
    setUiPrefs(parseUiPrefs(profile.ui_prefs));
    setError('');
    setSaved(false);
  }, [open, profile]);

  useEffect(() => {
    if (!open || !profile?.organisation_id) {
      setOrgName('');
      return;
    }
    let cancelled = false;
    (async () => {
      const { data } = await sb
        .from('organisations')
        .select('name')
        .eq('id', profile.organisation_id)
        .maybeSingle();
      if (!cancelled) setOrgName(data?.name || '');
    })();
    return () => { cancelled = true; };
  }, [open, profile?.organisation_id]);

  if (!open) return null;

  async function persist(patch, successMessage = true) {
    if (!profile?.id) return;
    setSaving(true);
    setError('');
    setSaved(false);
    const { error: saveError } = await sb.from('profiles').update(patch).eq('id', profile.id);
    setSaving(false);
    if (saveError) {
      setError(saveError.message);
      return;
    }
    if (successMessage) setSaved(true);
    await refreshProfile?.();
  }

  async function saveGeneral() {
    await persist({
      full_name: fullName.trim() || null,
      preferred_name: preferredName.trim() || null,
      default_landing_page: landingPage,
      timezone,
    });
  }

  async function saveNotifications() {
    await persist({ notification_prefs: notifications });
  }

  async function saveDefaults() {
    await persist({ language, ui_prefs: uiPrefs });
  }

  return (
    <div className="modal-overlay settings-modal-overlay" onClick={onClose}>
      <div className="settings-modal" onClick={(e) => e.stopPropagation()}>
        <aside className="settings-rail">
          <div className="settings-rail-label">Settings</div>
          {SECTIONS.map((s) => (
            <button
              key={s.id}
              type="button"
              className={`settings-rail-item${section === s.id ? ' active' : ''}`}
              onClick={() => { setSection(s.id); setSaved(false); setError(''); }}
            >
              {s.label}
            </button>
          ))}
        </aside>
        <div className="settings-content">
          <button type="button" className="settings-close" onClick={onClose} aria-label="Close settings">✕</button>

          {section === 'general' && (
            <>
              <h2 className="settings-h1">General</h2>
              <div className="settings-field">
                <label htmlFor="settings-full-name">Full name</label>
                <input
                  id="settings-full-name"
                  className="form-input"
                  value={fullName}
                  onChange={(e) => { setFullName(e.target.value); setSaved(false); }}
                />
              </div>
              <div className="settings-field">
                <label htmlFor="settings-display-name">Display name</label>
                <input
                  id="settings-display-name"
                  className="form-input"
                  value={preferredName}
                  onChange={(e) => { setPreferredName(e.target.value); setSaved(false); }}
                  placeholder="Short name shown in the sidebar"
                />
              </div>
              <div className="settings-field">
                <label htmlFor="settings-landing">Default landing page</label>
                <select
                  id="settings-landing"
                  className="form-select"
                  value={landingPage}
                  onChange={(e) => { setLandingPage(e.target.value); setSaved(false); }}
                >
                  <option value="home">Programmes &amp; projects</option>
                  <option value="last_project">Last opened project</option>
                </select>
              </div>
              <div className="settings-field">
                <label htmlFor="settings-timezone">Time zone</label>
                <select
                  id="settings-timezone"
                  className="form-select"
                  value={timezone}
                  onChange={(e) => { setTimezone(e.target.value); setSaved(false); }}
                >
                  {TIMEZONES.map((tz) => (
                    <option key={tz} value={tz}>{tz}</option>
                  ))}
                </select>
              </div>
            </>
          )}

          {section === 'account' && (
            <>
              <h2 className="settings-h1">Account</h2>
              <div className="settings-field">
                <label>Email</label>
                <input className="form-input" value={user?.email || profile?.email || ''} readOnly />
              </div>
              <div className="settings-field">
                <label>Role</label>
                <div><Badge>{profile?.platform_role || 'viewer'}</Badge></div>
              </div>
              {orgName && (
                <div className="settings-field">
                  <label>Organisation</label>
                  <input className="form-input" value={orgName} readOnly />
                </div>
              )}
            </>
          )}

          {section === 'notifications' && (
            <>
              <h2 className="settings-h1">Notifications</h2>
              <p className="settings-section-intro">Choose which email notifications you receive.</p>
              <ToggleRow
                label="Stage gate reminders"
                hint="Upcoming gate meetings and readiness deadlines"
                checked={notifications.gate_reminders}
                onChange={(v) => { setNotifications((p) => ({ ...p, gate_reminders: v })); setSaved(false); }}
              />
              <ToggleRow
                label="RAID digest"
                hint="Weekly summary of open risks, actions, issues, and decisions"
                checked={notifications.raid_digest}
                onChange={(v) => { setNotifications((p) => ({ ...p, raid_digest: v })); setSaved(false); }}
              />
              <ToggleRow
                label="Status report due"
                hint="Reminder when a programme status report is overdue"
                checked={notifications.status_report_due}
                onChange={(v) => { setNotifications((p) => ({ ...p, status_report_due: v })); setSaved(false); }}
              />
            </>
          )}

          {section === 'defaults' && (
            <>
              <h2 className="settings-h1">Defaults</h2>
              <p className="settings-section-intro">Personal defaults applied when you open Transformax.</p>
              <div className="settings-field">
                <label htmlFor="settings-language">Language</label>
                <select
                  id="settings-language"
                  className="form-select"
                  value={language}
                  onChange={(e) => { setLanguage(e.target.value); setSaved(false); }}
                >
                  {LANGUAGES.map((lang) => (
                    <option key={lang.value} value={lang.value}>{lang.label}</option>
                  ))}
                </select>
              </div>
              <div className="settings-field">
                <label htmlFor="settings-prog-filter">Programme list filter</label>
                <select
                  id="settings-prog-filter"
                  className="form-select"
                  value={uiPrefs.programme_filter}
                  onChange={(e) => {
                    setUiPrefs((p) => ({ ...p, programme_filter: e.target.value }));
                    setSaved(false);
                  }}
                >
                  <option value="active">Active programmes only</option>
                  <option value="closed">Closed programmes only</option>
                </select>
              </div>
              <ToggleRow
                label="Start with sidebar collapsed"
                hint="Layer 1 and project workspaces open with a narrow sidebar"
                checked={uiPrefs.sidebar_collapsed}
                onChange={(v) => { setUiPrefs((p) => ({ ...p, sidebar_collapsed: v })); setSaved(false); }}
              />
            </>
          )}

          {error && <p className="settings-error">{error}</p>}
          {saved && <p className="settings-saved">Saved.</p>}

          {section !== 'account' && (
            <div style={{ marginTop: 8 }}>
              <button
                type="button"
                className="btn-primary"
                disabled={saving}
                onClick={() => {
                  if (section === 'general') saveGeneral();
                  else if (section === 'notifications') saveNotifications();
                  else if (section === 'defaults') saveDefaults();
                }}
              >
                {saving ? 'Saving…' : 'Save changes'}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
